import {
  type CallWindowJobCap,
  type CallWindowJobCapFilter,
  type CallWindowJobCapList,
} from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';

import { CallWindowJobCapClientService } from '../../tools/helpers';
import { queryKeys } from './constants';

export const useCallWindowJobCapBatchQuery = ({
  filter,
  enabled = true,
  options,
}: {
  filter: CallWindowJobCapFilter;
  enabled?: boolean;
  options?: Partial<UseQueryOptions<CallWindowJobCapList, Error>>;
}) => {
  return useQuery({
    queryFn: async () => {
      return await CallWindowJobCapClientService.loadCallWindowJobCaps({ filter });
    },
    queryKey: [
      queryKeys.callWindowJobCap.root,
      queryKeys.callWindowJobCap.list,
      queryKeys.callWindowJobCap.getHash(filter),
    ],
    enabled,
    ...options,
  });
};

/**
 * Daily job-type caps for one department and trade, resolved as dayOfWeek -> jobTypeId -> maxPerDay.
 *
 * Loads every day in one request and indexes client-side rather than filtering by day on the
 * server: day 0 (Sunday) is indistinguishable from "unset" to the backend query builder unless
 * a field mask is passed, and callers here want more than one day anyway. A department has at
 * most 7 x jobTypes cap rows, so the whole set is cheap to hold.
 *
 * Shares its query key with other callers, so react-query dedupes the fetch.
 */
export const useDailyJobTypeCaps = ({
  departmentId,
  eventTradeId,
  enabled = true,
}: {
  departmentId?: number;
  /** The department's trade; 0 or omitted loads every trade's caps. */
  eventTradeId?: number;
  enabled?: boolean;
}) => {
  const deptId = Number(departmentId) || 0;
  const { data } = useCallWindowJobCapBatchQuery({
    filter: { departmentId: deptId, eventTradeId: Number(eventTradeId) || 0, isActive: true },
    enabled: enabled && deptId > 0,
  });

  return useMemo(() => {
    const byDay: Record<number, Record<number, number>> = {};
    (data?.results ?? []).forEach((c) => {
      if (!byDay[c.dayOfWeek]) byDay[c.dayOfWeek] = {};
      byDay[c.dayOfWeek][c.jobTypeId] = c.maxPerDay;
    });
    return byDay;
  }, [data]);
};

export const useCallWindowJobCapCreateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      cap: Omit<
        CallWindowJobCap,
        'id' | 'createdAt' | 'updatedAt' | 'fieldMask' | 'pageNumber' | 'orderBy' | 'orderDir'
      >,
    ) => {
      const req = {
        id: 0,
        createdAt: '',
        updatedAt: '',
        fieldMask: [],
        pageNumber: 0,
        orderBy: '',
        orderDir: '',
        ...cap,
      };
      return await CallWindowJobCapClientService.Create(req);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.callWindowJobCap.root] });
    },
  });
};

export const useCallWindowJobCapUpdateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cap: CallWindowJobCap) => {
      return await CallWindowJobCapClientService.Update(cap);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.callWindowJobCap.root] });
    },
  });
};

export const useCallWindowJobCapDeleteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const req = {
        id,
        departmentId: 0,
        eventTradeId: 0,
        dayOfWeek: 0,
        jobTypeId: 0,
        maxPerDay: 0,
        createdAt: '',
        updatedAt: '',
        isActive: true,
        fieldMask: [],
        pageNumber: 0,
        orderBy: '',
        orderDir: '',
      };
      return await CallWindowJobCapClientService.Delete(req);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.callWindowJobCap.root] });
    },
  });
};
